import { usePaymentProviderState } from "checkout/payment/PaymentProviderState"
import { useTranslation } from "react-i18next"
import { useAxiosClient } from "../../AxiosClient"
import { useBorgunPaymentApi } from "../../borgun/BorgunPaymentProvider"
import {
  useCartService,
  useCompleteCartCheckoutService
} from "../../cart/CartServiceHook"
import { useCartValidationApi } from "../../cart/CartValidation"
import {
  useCartLink,
  useCustomerInformationLink,
  usePaymentLink,
  useProcessingLink
} from "../../components/Links"
import { useCustomerService } from "../../customer/CustomerService"
import { ErrorType } from "../../error/ErrorType"
import { usePreferredLanguage } from "../../i18n/StoreHooks"
import { useRouter } from "../../routing/useRouter"
import { useSentryLogging } from "../../sentry/CaptureMessage"
import { useAppDispatch } from "../../State"
import { useInformationPageState } from "../customerInformation/InformationPageState"
import { useGiftCardState } from "../payment/GiftCardState"
import { useErrorMessaging } from "../validation/ErrorMessaging"
import { CartValidation } from "../validation/FormValidator"
import { ValidationError } from "../validation/ValidationError"
import { ValidationException } from "../validation/ValidationException"
import { BillingAddressValidation } from "./validators/BillingAddressValidation"
import { BorgunValidation } from "./validators/BorgunPaymentValidation"
import { CartOrderableValidation } from "./validators/CartOrderableValidation"
import { DiscountCodeValidation } from "./validators/DiscountCodeValidation"
import { EmailValidation } from "./validators/EmailValidation"
import { EmptyCartValidation } from "./validators/EmptyCartValidation"
import { EmptyPaymentValidation } from "./validators/EmptyPaymentValidation"
import { FraudValidation } from "./validators/FraudValidation"
import { GACustomDataValidation } from "./validators/GACustomDataValidation"
import { GiftCardValidation } from "./validators/GiftCardValidation"
import { LineItemValidation } from "./validators/LineItemValidation"
import { SaveNewCustomerAddressValidation } from "./validators/SaveNewCustomerAddressValidation"
import { ShippingAddressValidation } from "./validators/ShippingAddressValidation"
import { ShippingMethodValidation } from "./validators/ShippingMethodValidation"

export const useCheckoutReviewValidation = (): CartValidation => {
  const paymentProviders = usePaymentProviderState()

  const { t } = useTranslation()
  const axiosClient = useAxiosClient()
  const dispatch = useAppDispatch()
  const { captureMessage } = useSentryLogging()

  const customerService = useCustomerService()
  const informationPageState = useInformationPageState()
  const giftcardState = useGiftCardState()

  const cartService = useCartService()
  const { validateCart } = useCartValidationApi()
  const language = usePreferredLanguage()
  const { validateFraud } = useCompleteCartCheckoutService()
  const borgunPaymentApi = useBorgunPaymentApi()

  const emptyCartValidation = new EmptyCartValidation(t)
  const emailValidation = new EmailValidation(t)
  const shippingAddressValidation = new ShippingAddressValidation(t)
  const billingAddressValidation = new BillingAddressValidation(t)
  const shippingMethodValidation = new ShippingMethodValidation(t)
  const saveNewAddressValidation = new SaveNewCustomerAddressValidation(
    t,
    informationPageState,
    customerService
  )
  const lineItemValidation = new LineItemValidation(t, validateCart, language)
  const discountCodeValidation = new DiscountCodeValidation(t, cartService)
  const fraudValidation = new FraudValidation(validateFraud)
  const gaCustomDataValidation = new GACustomDataValidation(cartService)
  const emptyPaymentProviderValidation = new EmptyPaymentValidation(
    t,
    paymentProviders
  )
  const cartOrderableValidation = new CartOrderableValidation(t, captureMessage)
  const giftCardValidation = new GiftCardValidation(
    t,
    giftcardState,
    axiosClient,
    dispatch,
    paymentProviders
  )
  const borgunValidation = new BorgunValidation(
    t,
    borgunPaymentApi,
    paymentProviders
  )

  return emptyCartValidation
    .then(emailValidation)
    .then(shippingAddressValidation)
    .then(billingAddressValidation)
    .then(shippingMethodValidation)
    .then(saveNewAddressValidation)
    .then(lineItemValidation)
    .then(discountCodeValidation)
    .then(fraudValidation)
    .then(gaCustomDataValidation)
    .then(emptyPaymentProviderValidation)
    .then(cartOrderableValidation)
    .then(giftCardValidation)
    .then(borgunValidation)
}

export const usePaymentErrorHandling = () => {
  const { catchValidationException } = useErrorMessaging()
  const { getErrorRedirect } = useReviewErrorRedirects()
  const paymentLink = usePaymentLink()
  const router = useRouter()

  const onPaymentError = err => {
    catchValidationException(err)
    if (err instanceof ValidationException) {
      const redirect = getErrorRedirect(err.validationError) || paymentLink
      if (redirect) {
        return router.push(redirect.href, redirect.as)
      }
    }
    return undefined
  }

  return { onPaymentError }
}

export const useReviewErrorRedirects = () => {
  const cartLink = useCartLink()
  const customerInformationLink = useCustomerInformationLink()
  const paymentLink = usePaymentLink()
  const processingLink = useProcessingLink()

  return {
    getErrorRedirect: (
      error: ValidationError
    ): { href: string; as: string } | undefined => {
      if (error[ErrorType.BillingAddressError]) {
        return paymentLink
      }
      if (error[ErrorType.CartValidationError]) {
        return cartLink
      }
      if (error[ErrorType.CustomerInformationError]) {
        return customerInformationLink
      }
      if (error[ErrorType.GiftCardError]) {
        return paymentLink
      }
      if (error[ErrorType.PaymentError]) {
        return paymentLink
      }
      if (error[ErrorType.ShippingMethodError]) {
        return customerInformationLink
      }
      if (error[ErrorType.CartAlreadyOrderedError]) {
        return processingLink
      }
      return undefined
    }
  }
}
